import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Loader2, Trash2, Eye, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isFileDocument } from "@/lib/validation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ZoomableImage } from "@/components/ui/ZoomableImage";
import { deleteOrderDocument, resolveOrderS3Context } from "@/lib/order-doc-delete";

type DocType = "bol" | "tender";

const docConfig = {
  bol: {
    label: "Bill of Lading",
    shortLabel: "BoL",
    table: "order_bol_images" as const,
    bucket: "bol-images",
    queryKey: "bol-images",
    countQueryKey: "bol-images-count",
  },
  tender: {
    label: "Tender",
    shortLabel: "Tender",
    table: "order_tender_images" as const,
    bucket: "tender-images",
    queryKey: "tender-images",
    countQueryKey: "tender-images-count",
  },
};

async function getSignedUrl(bucket: string, imagePath: string): Promise<string | null> {
  if (imagePath.startsWith("http")) return imagePath;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(imagePath, 3600);
  if (error) {
    console.error("Failed to create signed URL:", error);
    return null;
  }
  return data.signedUrl;
}

interface DriverDocViewerDialogProps {
  orderId: string;
  docType: DocType;
  imageCount: number;
}

export function DriverDocViewerDialog({ orderId, docType, imageCount }: DriverDocViewerDialogProps) {
  const cfg = docConfig[docType];
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; path: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();

  const { data: images, isLoading } = useQuery({
    queryKey: [cfg.queryKey, orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(cfg.table)
        .select("id, image_url, image_path, created_at")
        .eq("order_id", orderId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: isOpen,
  });

  useEffect(() => {
    if (images && images.length > 0) {
      (async () => {
        const urls: Record<string, string> = {};
        for (const img of images) {
          const path = img.image_path || img.image_url;
          if (path) {
            const url = await getSignedUrl(cfg.bucket, path);
            if (url) urls[img.id] = url;
          }
        }
        setSignedUrls(urls);
      })();
    }
  }, [images, cfg.bucket]);

  // Clamp index when images change
  useEffect(() => {
    if (images && currentIndex >= images.length && images.length > 0) {
      setCurrentIndex(images.length - 1);
    }
  }, [images, currentIndex]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      // Sync-then-Persist: the NetSuite-facing S3 object must go first,
      // otherwise the file is orphaned in the bucket.
      const ctx = await resolveOrderS3Context(orderId);
      await deleteOrderDocument({
        table: cfg.table,
        bucket: cfg.bucket,
        rowId: deleteTarget.id,
        imagePath: deleteTarget.path,
        orderUniqueId: ctx.orderUniqueId,
        tenantId: ctx.tenantId,
        tenantSlug: ctx.tenantSlug,
      });
      toast.success(`${cfg.shortLabel} image deleted`);
      queryClient.invalidateQueries({ queryKey: [cfg.queryKey, orderId] });
      queryClient.invalidateQueries({ queryKey: [cfg.countQueryKey, orderId] });
    } catch (err: any) {
      console.error("Delete error:", err);
      toast.error(`Failed to delete ${cfg.shortLabel} image: ${err?.message ?? "Unknown error"}`);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (imageCount === 0) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="w-full">
            <Eye className="h-4 w-4 mr-2" />
            View {cfg.shortLabel} ({imageCount})
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{cfg.label}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto">
            {isLoading || !images ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : images.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No images found.</p>
            ) : (
              <div className="space-y-4">
                {/* Image viewer */}
                <div className="relative bg-muted/30 rounded-lg overflow-hidden">
                  {images[currentIndex] && signedUrls[images[currentIndex].id] ? (
                    (() => {
                      const filePath = images[currentIndex].image_path || images[currentIndex].image_url;
                      const url = signedUrls[images[currentIndex].id];
                      if (filePath.toLowerCase().endsWith('.pdf')) {
                        return (
                          <iframe
                            src={url}
                            title={`${cfg.label} ${currentIndex + 1}`}
                            className="w-full h-[60vh] border-0"
                          />
                        );
                      }
                      if (isFileDocument(filePath)) {
                        return (
                          <div className="flex flex-col items-center justify-center py-12 gap-4">
                            <FileText className="h-16 w-16 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              {filePath.split('/').pop()}
                            </p>
                            <Button asChild variant="outline" size="sm">
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                Download to View
                              </a>
                            </Button>
                          </div>
                        );
                      }
                      return (
                        <ZoomableImage
                          src={url}
                          openUrl={url}
                          fileName={filePath.split("/").pop() || `${cfg.shortLabel}.jpg`}
                          alt={`${cfg.label} ${currentIndex + 1}`}
                          className="h-[60vh] w-full"
                        />
                      );
                    })()
                  ) : (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {images.length > 1 && (
                    <>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute left-2 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
                        onClick={() => setCurrentIndex((p) => (p > 0 ? p - 1 : images.length - 1))}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
                        onClick={() => setCurrentIndex((p) => (p < images.length - 1 ? p + 1 : 0))}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </>
                  )}
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                  <div className="flex gap-2 justify-center flex-wrap">
                    {images.map((img, index) => {
                      const filePath = img.image_path || img.image_url;
                      const isDoc = isFileDocument(filePath);
                      return (
                        <button
                          key={img.id}
                          onClick={() => setCurrentIndex(index)}
                          className={cn(
                            "w-16 h-16 rounded-md overflow-hidden border-2 transition-all",
                            index === currentIndex
                              ? "border-primary ring-2 ring-primary/20"
                              : "border-transparent opacity-60 hover:opacity-100"
                          )}
                        >
                          {isDoc ? (
                            <div className="w-full h-full bg-muted flex flex-col items-center justify-center gap-0.5">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                              <span className="text-[9px] text-muted-foreground uppercase">
                                {filePath.split('.').pop()}
                              </span>
                            </div>
                          ) : signedUrls[img.id] ? (
                            <img src={signedUrls[img.id]} alt={`Thumb ${index + 1}`} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-muted flex items-center justify-center">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Counter + Delete */}
                <div className="flex items-center justify-between px-2">
                  <p className="text-sm text-muted-foreground">
                    {currentIndex + 1} of {images.length}
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const img = images[currentIndex];
                      setDeleteTarget({ id: img.id, path: img.image_path || img.image_url });
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {cfg.shortLabel} image?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this image. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
